import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import apiService, { Product } from '../../services/api';
import analytics from '../../services/analytics';

const SEOPageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const SEOHero = styled.section`
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  padding: var(--spacing-16) 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" patternUnits="userSpaceOnUse" width="100" height="100"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
    position: relative;
    z-index: 1;
  }

  .hero-content {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: var(--spacing-8);
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      text-align: center;
    }
  }

  .hero-text {
    h1 {
      font-size: 3.5rem;
      font-weight: 800;
      margin-bottom: var(--spacing-4);
      line-height: 1.1;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.3);

      @media (max-width: 768px) {
        font-size: 2.5rem;
      }
    }

    .subtitle {
      font-size: 1.3rem;
      opacity: 0.95;
      margin-bottom: var(--spacing-6);
      line-height: 1.6;
    }

    .features-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-2);
        font-size: 1.1rem;

        &::before {
          content: '✨';
          margin-right: var(--spacing-2);
        }
      }
    }
  }

  .hero-stats {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: var(--border-radius-lg);
    padding: var(--spacing-6);
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.2);

    .stat {
      margin-bottom: var(--spacing-4);

      &:last-child {
        margin-bottom: 0;
      }

      .number {
        display: block;
        font-size: 2.5rem;
        font-weight: 800;
        line-height: 1;
        margin-bottom: var(--spacing-1);
      }

      .label {
        font-size: 0.9rem;
        opacity: 0.8;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }
  }
`;

const CollectionShowcase = styled.section`
  padding: var(--spacing-16) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  .section-header {
    text-align: center;
    margin-bottom: var(--spacing-12);

    h2 {
      font-size: 2.8rem;
      color: var(--color-secondary);
      margin-bottom: var(--spacing-4);
    }

    .description {
      font-size: 1.2rem;
      color: var(--color-text-light);
      max-width: 700px;
      margin: 0 auto;
      line-height: 1.7;
    }
  }
`;

const ProductShowcase = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-8);
  margin-bottom: var(--spacing-12);
`;

const FeaturedProduct = styled.div`
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }

  .product-badge {
    position: absolute;
    top: var(--spacing-4);
    right: var(--spacing-4);
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
    color: white;
    padding: var(--spacing-2) var(--spacing-3);
    border-radius: var(--border-radius-full);
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 2;
  }

  .product-image {
    width: 100%;
    height: 280px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 5rem;
    position: relative;
    overflow: hidden;
    padding: var(--spacing-4);
    box-sizing: border-box;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      transition: transform 0.4s ease;
    }

    &:hover img {
      transform: scale(1.08);
    }
  }

  .product-content {
    padding: var(--spacing-6);

    .product-name {
      font-size: 1.4rem;
      font-weight: 700;
      color: var(--color-secondary);
      margin-bottom: var(--spacing-3);
      line-height: 1.3;
    }

    .product-description {
      color: var(--color-text-light);
      line-height: 1.6;
      margin-bottom: var(--spacing-4);
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .product-features {
      list-style: none;
      padding: 0;
      margin: 0 0 var(--spacing-4) 0;

      li {
        color: var(--color-text-light);
        font-size: 0.9rem;
        margin-bottom: var(--spacing-1);
        position: relative;
        padding-left: var(--spacing-4);

        &::before {
          content: '•';
          color: var(--color-secondary);
          font-weight: 700;
          position: absolute;
          left: 0;
        }
      }
    }

    .product-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: var(--spacing-4);
      border-top: 1px solid #f1f3f4;

      .price-section {
        .current-price {
          font-size: 1.6rem;
          font-weight: 800;
          color: var(--color-secondary);
          display: block;
        }

        .original-price {
          font-size: 1rem;
          color: var(--color-text-light);
          text-decoration: line-through;
          margin-right: var(--spacing-2);
        }

        .savings {
          font-size: 0.9rem;
          color: #28a745;
          font-weight: 600;
        }
      }

      .view-btn {
        background: var(--color-secondary);
        color: white;
        border: none;
        padding: var(--spacing-3) var(--spacing-5);
        border-radius: var(--border-radius-full);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-size: 0.9rem;

        &:hover {
          background: #1f1e3a;
          transform: translateX(3px);
        }
      }
    }
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-6);
  margin-top: var(--spacing-8);
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  padding: var(--spacing-6);
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }

  .category-icon {
    font-size: 3rem;
    margin-bottom: var(--spacing-4);
    display: block;
  }

  .category-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-2);
  }

  .category-count {
    color: var(--color-text-light);
    font-size: 0.9rem;
    margin-bottom: var(--spacing-3);
  }

  .category-description {
    color: var(--color-text-light);
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const TrustSection = styled.section`
  background: white;
  padding: var(--spacing-16) 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    text-align: center;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-12);
    font-size: 2.5rem;
  }
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-8);
`;

const TrustCard = styled.div`
  text-align: center;
  padding: var(--spacing-6);

  .trust-icon {
    font-size: 4rem;
    margin-bottom: var(--spacing-4);
    display: block;
  }

  h3 {
    font-size: 1.4rem;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-3);
  }

  p {
    color: var(--color-text-light);
    line-height: 1.7;
    font-size: 1.1rem;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, var(--color-secondary) 0%, #1f1e3a 100%);
  color: white;
  padding: var(--spacing-16) 0;
  text-align: center;

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 var(--spacing-6);
  }

  h2 {
    font-size: 3rem;
    margin-bottom: var(--spacing-6);
    font-weight: 700;
  }

  p {
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: var(--spacing-8);
    line-height: 1.6;
  }

  .cta-buttons {
    display: flex;
    gap: var(--spacing-4);
    justify-content: center;
    flex-wrap: wrap;
  }

  .cta-button {
    background: white;
    color: var(--color-secondary);
    border: none;
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-full);
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-2);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    }

    &.secondary {
      background: transparent;
      color: white;
      border: 2px solid rgba(255,255,255,0.3);

      &:hover {
        background: rgba(255,255,255,0.1);
        border-color: white;
      }
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  font-size: 1.2rem;
  color: var(--color-text-light);
`;

const SEOProductCollectionPage: React.FC = () => {
  const { collection } = useParams<{ collection: string }>();
  const navigate = useNavigate();
  type CollectionProduct = Product & { id: number };
  const [products, setProducts] = useState<CollectionProduct[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollectionData();

    // Track page view for SEO analytics
    analytics.trackPageView(`${getCollectionTitle()} | Exklusiva Inredningsprodukter | Lillaflora`);
  }, [collection]);

  const loadCollectionData = async () => {
    setLoading(true);

    try {
      // Load all products and categories
      const [productsData, categoriesData] = await Promise.all([
        apiService.getProducts(),
        apiService.getCategories()
      ]);

      // Get featured products (those with discounts or marked as featured)
      const featuredProducts = (productsData || [])
        .filter((product): product is CollectionProduct =>
          (((product.is_featured ?? false) || (product.has_active_discount ?? false)) &&
          typeof product.id === 'number' &&
          (product.inventory_quantity ?? 0) > 0)
        )
        .slice(0, 6);

      setProducts(featuredProducts);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading collection data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCollectionTitle = () => {
    if (collection === 'premium-inredning') return 'Premium Inredning';
    if (collection === 'svenska-favoriter') return 'Svenska Favoriter';
    if (collection === 'mysiga-hem') return 'Mysiga Hem';
    return 'Våra Produkter';
  };

  const getCollectionDescription = () => {
    if (collection === 'premium-inredning') {
      return 'Upptäck vårt handplockade sortiment av premium inredningsprodukter som förvandlar ditt hem till en elegant oas av skandinavisk design.';
    }
    if (collection === 'svenska-favoriter') {
      return 'Klassiska svenska produkter som har värmt svenska hem i generationer. Tidlös design och oöverträffad kvalitet.';
    }
    if (collection === 'mysiga-hem') {
      return 'Skapa den perfekta mys-känslan i ditt hem med våra doftljus, textiler och dekorationer som utstrålar värme och gemenskap.';
    }
    return 'Utforska vårt noggrant kurerade sortiment av inredningsprodukter för det moderna svenska hemmet.';
  };

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('ljus') || name.includes('doft')) return '🕯️';
    if (name.includes('dukning') || name.includes('servering')) return '🍽️';
    if (name.includes('växter') || name.includes('konstväxter')) return '🌿';
    if (name.includes('textiler')) return '🧵';
    if (name.includes('muggar') || name.includes('koppar')) return '☕';
    if (name.includes('godis')) return '🍬';
    if (name.includes('lyktor') || name.includes('dekoration')) return '🏮';
    return '✨';
  };

  const handleProductClick = (product: CollectionProduct) => {
    navigate(`/produkter/${product.slug}`);
  };

  const handleCategoryClick = (category: any) => {
    navigate(`/produkter?kategori=${category.slug}`);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(price);
  };

  if (loading) {
    return (
      <SEOPageWrapper>
        <LoadingWrapper>Laddar kollektion...</LoadingWrapper>
      </SEOPageWrapper>
    );
  }

  const metaTitle = `${getCollectionTitle()} | Exklusiva Inredningsprodukter | Lillaflora`;
  const metaDescription = getCollectionDescription();
  const canonicalUrl = `https://lillaflora.nu/kollektion/${collection}`;

  return (
    <SEOPageWrapper>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${getCollectionTitle()}, inredning, svenska produkter, design, hemkänsla, lillaflora`} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": getCollectionTitle(),
            "description": metaDescription,
            "url": canonicalUrl,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": products.length,
              "itemListElement": products.map((product, index) => ({
                "@type": "Product",
                "position": index + 1,
                "name": product.name,
                "description": product.short_description,
                "offers": {
                  "@type": "Offer",
                  "price": product.final_price || product.price,
                  "priceCurrency": "SEK",
                  "availability": (product.inventory_quantity ?? 0) > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock"
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <SEOHero>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>{getCollectionTitle()}</h1>
              <p className="subtitle">{getCollectionDescription()}</p>
              <ul className="features-list">
                <li>Handplockade produkter av högsta kvalitet</li>
                <li>Snabb leverans inom 1-2 arbetsdagar</li>
                <li>14 dagars öppet köp</li>
                <li>Personlig kundservice</li>
              </ul>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="number">{products.length}+</span>
                <span className="label">Utvalda Produkter</span>
              </div>
              <div className="stat">
                <span className="number">{categories.length}</span>
                <span className="label">Kategorier</span>
              </div>
              <div className="stat">
                <span className="number">98%</span>
                <span className="label">Nöjda Kunder</span>
              </div>
            </div>
          </div>
        </div>
      </SEOHero>

      <CollectionShowcase>
        <div className="container">
          <div className="section-header">
            <h2>Utvalda Favoriter</h2>
            <p className="description">
              Dessa produkter har blivit våra kunders absoluta favoriter tack vare sin kombination
              av vacker design, hög kvalitet och fantastiska värde för pengarna.
            </p>
          </div>

          <ProductShowcase>
            {products.map((product, index) => (
              <FeaturedProduct key={product.id} onClick={() => handleProductClick(product)}>
                {product.has_active_discount && (
                  <div className="product-badge">REA</div>
                )}
                {product.is_featured && !product.has_active_discount && (
                  <div className="product-badge">Populär</div>
                )}

                <div className="product-image">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.innerHTML = '✨';
                      }}
                    />
                  ) : (
                    '✨'
                  )}
                </div>

                <div className="product-content">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">
                    {product.short_description || product.description}
                  </p>

                  {product.categories && product.categories.length > 0 && (
                    <ul className="product-features">
                      <li>Kategori: {product.categories.join(', ')}</li>
                      <li>Högkvalitativ design</li>
                      <li>Snabb leverans</li>
                    </ul>
                  )}

                  <div className="product-footer">
                    <div className="price-section">
                      <span className="current-price">
                        {formatPrice(product.final_price || product.price)}
                      </span>
                      {product.has_active_discount && product.final_price !== null && product.final_price !== undefined && product.price !== product.final_price && (
                        <div>
                          <span className="original-price">
                            {formatPrice(product.price)}
                          </span>
                          <span className="savings">
                            Spara {formatPrice(product.price - (product.final_price ?? product.price))}
                          </span>
                        </div>
                      )}
                    </div>
                    <button className="view-btn">
                      Visa mer →
                    </button>
                  </div>
                </div>
              </FeaturedProduct>
            ))}
          </ProductShowcase>

          <div className="section-header">
            <h2>Shoppa efter kategori</h2>
            <p className="description">
              Utforska vårt sortiment organiserat i tematiska kategorier för att enkelt hitta
              precis det du söker för ditt hem.
            </p>
          </div>

          <CategoryGrid>
            {categories.map((category) => (
              <CategoryCard key={category.id} onClick={() => handleCategoryClick(category)}>
                <span className="category-icon">{getCategoryIcon(category.name)}</span>
                <h3 className="category-name">{category.name}</h3>
                <p className="category-count">Upptäck sortimentet</p>
                <p className="category-description">
                  {category.description || `Utforska våra ${category.name.toLowerCase()}`}
                </p>
              </CategoryCard>
            ))}
          </CategoryGrid>
        </div>
      </CollectionShowcase>

      <TrustSection>
        <div className="container">
          <h2>Därför väljer tusentals kunder Lillaflora</h2>
          <TrustGrid>
            <TrustCard>
              <span className="trust-icon">🏆</span>
              <h3>Garanterad kvalitet</h3>
              <p>
                Vi testar alla produkter själva innan de hamnar i vårt sortiment.
                Endast produkter som lever upp till våra höga standarder får plats i vår webshop.
              </p>
            </TrustCard>
            <TrustCard>
              <span className="trust-icon">🚚</span>
              <h3>Blixtsnabb leverans</h3>
              <p>
                Beställ före 14:00 så skickar vi ditt paket samma dag.
                De flesta kunder får sina produkter redan nästa arbetsdag.
              </p>
            </TrustCard>
            <TrustCard>
              <span className="trust-icon">💝</span>
              <h3>Personlig service</h3>
              <p>
                Vårt team hjälper dig gärna med produktråd, storlek och stil.
                Vi ser till att du blir 100% nöjd med ditt köp.
              </p>
            </TrustCard>
          </TrustGrid>
        </div>
      </TrustSection>

      <CTASection>
        <div className="container">
          <h2>Börja skapa ditt drömhem idag</h2>
          <p>
            Med över {products.length} noggrant utvalda produkter och expertråd från vårt team
            är du bara några klick från att förvandla ditt hem.
          </p>
          <div className="cta-buttons">
            <button
              className="cta-button"
              onClick={() => navigate('/produkter')}
            >
              Utforska alla produkter →
            </button>
            <button
              className="cta-button secondary"
              onClick={() => navigate('/kontakt')}
            >
              Få personlig rådgivning 💬
            </button>
          </div>
        </div>
      </CTASection>
    </SEOPageWrapper>
  );
};

export default SEOProductCollectionPage;
